import { Currency, useMutation } from '@sequencehq/api'
import { dashboardv99990101Client } from '@sequencehq/api/dashboard/v99990101'
import {
  useNotifications,
  SimpleModal,
  Flex
} from '@sequencehq/core-components'
import { formatISODate, useForm } from '@sequencehq/utils'
import { required } from '@sequencehq/utils/validation'
import { CurrencyInputSelectControl } from 'components/CurrencyInputSelectControl/CurrencyInputSelectControl'
import { CustomerSelect } from 'Customer/index'
import { useNavigate } from 'react-router-dom'
import { getDefaultCreditNoteBillingPeriod } from 'CreditNotes/utils/getDefaultCreditNoteBillingPeriod.ts'

type FormValues = {
  currency: Currency
  customerId: string
}

interface Props {
  onClose: () => void
  customer?: { id: string }
  currency?: Currency
}

/**
 * Mount this modal only while it should be visible: it renders with a
 * constant `isOpen` and the parent unmounts it via `onClose`, resetting the
 * form state for the next open.
 */
export const CreateCreditNoteModal = ({
  onClose,
  customer = { id: '' },
  currency = 'USD'
}: Props) => {
  const createCreditNote = useCreateCreditNote()
  const navigate = useNavigate()

  const form = useForm<FormValues>({
    value: {
      currency: currency,
      customerId: customer.id
    },
    fieldConfiguration: [
      {
        property: 'currency',
        validation: [required]
      },
      {
        property: 'customerId',
        validation: [required]
      }
    ]
  })

  const handleSubmit = async () => {
    const creditNote = await createCreditNote({
      currency: form.queries.formData.currency,
      customerId: form.queries.formData.customerId
    })

    if (creditNote?.id) {
      navigate(`/credit-notes/${creditNote.id}`)
      return true
    }

    return false
  }

  const isPreselectedCustomer = customer.id !== ''

  return (
    <SimpleModal
      isOpen
      onClose={onClose}
      data-testid="createCreditNote"
      title="Create credit note"
      cancelButtonText="Cancel"
      submitButtonText="Create credit note"
      submittingButtonText="Creating credit note..."
      submitDisabled={!form.queries.isValid}
      contentProps={{
        minHeight: '0px'
      }}
      onSubmit={handleSubmit}
    >
      <Flex flexDirection="column" gap="16px">
        <CurrencyInputSelectControl
          label="Currency"
          data-testid="createCreditNote.currency"
          value={form.fields.currency.value}
          onChange={form.fields.currency.onChange}
          validationErrors={form.fields.currency.validationErrors}
        />
        <CustomerSelect
          label="Customer"
          data-testid="createCreditNote.customer"
          value={form.fields.customerId.value}
          onChange={form.fields.customerId.onChange}
          validationErrors={form.fields.customerId.validationErrors}
          disabled={isPreselectedCustomer}
        />
      </Flex>
    </SimpleModal>
  )
}

const useCreateCreditNote = () => {
  const { displayNotification } = useNotifications()
  const { mutateAsync: createLineItemGroup } = useMutation(
    dashboardv99990101Client.postCreditNoteLineItemGroup
  )
  const { mutateAsync: createCreditNote } = useMutation(
    dashboardv99990101Client.postCreditNote
  )

  return async (input: { currency: Currency; customerId: string }) => {
    const { billingPeriodStart, billingPeriodEnd } =
      getDefaultCreditNoteBillingPeriod()

    const createdCreditNote = await createCreditNote({
      body: {
        currency: input.currency,
        customerId: input.customerId,
        accountingDate: formatISODate(new Date()),
        billingPeriodStart: billingPeriodStart,
        billingPeriodEnd: billingPeriodEnd,
        customerBillingAddress: undefined,
        customerEmails: undefined,
        customerLegalCompanyName: undefined,
        customerShippingAddress: undefined,
        memo: undefined,
        purchaseOrderNumber: undefined
      }
    })

    if (createdCreditNote === null) {
      displayNotification('Could not create credit note', {
        type: 'error'
      })
      return
    }

    // Creating a line item group is required for credit notes
    await createLineItemGroup({
      creditNoteId: createdCreditNote.id,
      body: {
        title: ''
      }
    })

    displayNotification('Credit note created', {
      type: 'success'
    })

    return createdCreditNote
  }
}
